import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, X, Plus, Loader2, ArrowDownRight, ArrowUpRight, AlertTriangle, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getWallet, getBillingConfig, goToCheckout, fetchTtsBlob, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

/** Header wallet: "Gettoni: 327" → panel with balance, history, extra packs (checkout MOCK). */
export function CreditsWallet() {
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [buying, setBuying] = useState(null);
  const [listening, setListening] = useState(false);
  const ref = useRef(null);
  const audioRef = useRef(null);

  const listen = async (text) => {
    if (listening || window.__nadiaSpeaking) return; // don't interrupt other readings
    try {
      setListening(true);
      window.__nadiaSpeaking = true;
      const blob = await fetchTtsBlob(text);
      const a = new Audio(URL.createObjectURL(blob));
      audioRef.current = a;
      const done = () => { setListening(false); window.__nadiaSpeaking = false; };
      a.onended = done;
      a.onerror = done;
      await a.play();
    } catch { setListening(false); window.__nadiaSpeaking = false; }
  };

  const load = () => getWallet().then(setW).catch(() => {});
  useEffect(() => {
    load();
    getBillingConfig().then(setCfg).catch(() => {});
    const t = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("nadia-wallet-refresh", onFocus);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); window.removeEventListener("nadia-wallet-refresh", onFocus); };
  }, []);
  useEffect(() => {
    const h = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useEffect(() => { if (open) load(); }, [open]);

  const buy = async (pack) => {
    setBuying(pack.id);
    try {
      await goToCheckout(nav, "credit_pack", pack.id);
    } catch (e) {
      toast.error(formatApiError(e, "Acquisto non riuscito"));
    } finally {
      setBuying(null);
    }
  };

  const b = w?.billing;
  if (w?.demo_unlimited) return (
    <div data-testid="owner-demo-badge" className="h-9 px-3 rounded-lg flex items-center text-sm font-semibold border border-cyan-400/30 bg-cyan-400/10 text-cyan-200" title="Account demo riservato: funzioni interne senza piano o consumo di gettoni">Demo · senza gettoni</div>
  );
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`h-9 px-3 rounded-lg flex items-center gap-2 text-sm font-semibold bg-white/[0.04] border transition-colors ${b?.low_credits ? "border-amber-400/60 text-amber-200" : "border-white/10 text-slate-100 hover:border-amber-400/40 hover:text-amber-200"}`}
        data-testid="wallet-button"
      >
        {b?.low_credits ? <AlertTriangle size={15} className="text-amber-300" data-testid="wallet-low-icon" /> : <Coins size={15} className="text-amber-300" />}
        {b ? <>Gettoni: <span data-testid="wallet-balance">{Number(b.credits_available).toLocaleString("it-IT", { maximumFractionDigits: 3 })}</span></> : "Attiva Nadia Cloud"}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-w-[92vw] rounded-2xl border border-white/10 bg-[#0F172A]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-40" data-testid="wallet-panel">
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
            <div className="text-sm font-heading font-semibold text-slate-100">Gettoni Nadia</div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-200"><X size={14} /></button>
          </div>
          {w?.owner_demo_status && !w.owner_demo_status.active && <div role="status" data-testid="owner-demo-status" className="mx-4 mt-4 rounded-xl border border-amber-400/40 p-3 text-xs text-amber-200"><p className="font-semibold">Demo riservata non attiva</p><p className="mt-1">{w.owner_demo_status.message}</p></div>}
          {!b ? (
            <div className="p-5 text-sm text-slate-400">
              Nessun piano attivo. <Link to="/piani" className="text-cyan-300 hover:text-cyan-200" data-testid="wallet-go-plans">Scegli Start o Pro</Link> per iniziare.
            </div>
          ) : (
            <>
              {b.low_credits && (
                <div className="mx-4 mt-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3 text-xs text-amber-100" data-testid="wallet-low-banner">
                  <div className="flex gap-2">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-300" />
                    <span><span className="font-semibold">Nadia:</span> {b.low_alert_text || `Ti restano ${Number(b.credits_available).toLocaleString("it-IT", { maximumFractionDigits: 3 })} gettoni. Nei nuovi piani anche la chat AI consuma gettoni. Ricarica per continuare.`}</span>
                  </div>
                  <button onClick={() => listen(b.low_alert_text || `Ti restano ${Number(b.credits_available).toLocaleString("it-IT", { maximumFractionDigits: 3 })} gettoni. Nei nuovi piani anche la chat AI consuma gettoni. Ricarica per continuare.`)} disabled={listening}
                    className="mt-2 ml-6 h-7 px-2.5 rounded-md border border-amber-400/40 text-amber-200 text-[11px] font-semibold inline-flex items-center gap-1 hover:bg-amber-500/10" data-testid="wallet-low-listen">
                    {listening ? <Loader2 size={11} className="animate-spin" /> : <Volume2 size={11} />} Ascolta
                  </button>
                </div>
              )}
              <div className="p-4 grid grid-cols-2 gap-3">
                <Stat label="Disponibili" value={b.credits_available} big testId="wallet-available" />
                {b.is_flex
                  ? <Stat label="Modalità" value="Nadia Flex" testId="wallet-included" sub="senza abbonamento" />
                  : <Stat label="Inclusi nel piano" value={b.credits_included} testId="wallet-included" sub={`Piano ${b.plan_name}`} />}
                <Stat label="Utilizzati" value={b.credits_used_cycle} testId="wallet-used" />
                {b.is_flex
                  ? <Stat label="Rinnovo" value="Nessuno" testId="wallet-renewal" sub="acquisti quando vuoi" />
                  : <Stat label="Prossimo rinnovo" value={fmtDate(b.renewal_date)} testId="wallet-renewal" sub={`+${b.credits_included} · max ${b.rollover_cap}`} />}
              </div>
              <div className="px-4 pb-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Storico</div>
              <div className="max-h-[200px] overflow-y-auto border-t border-white/5" data-testid="wallet-history">
                {(w.history || []).map((h) => (
                  <div key={h.id} className="px-4 py-2 flex items-center justify-between text-xs border-b border-white/5">
                    <span className="flex items-center gap-2 text-slate-300 min-w-0">
                      {h.type === "credit" ? <ArrowUpRight size={12} className="text-emerald-400 shrink-0" /> : <ArrowDownRight size={12} className="text-amber-400 shrink-0" />}
                      <span className="truncate">{h.label}</span>
                    </span>
                    <span className={`font-mono font-semibold shrink-0 ${h.type === "credit" ? "text-emerald-300" : "text-slate-200"}`}>{h.amount > 0 ? `+${h.amount}` : h.amount}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Aggiungi gettoni</div>
                <div className="grid grid-cols-3 gap-2" data-testid="wallet-packs">
                  {(cfg?.packs || []).map((p) => (
                    <button key={p.id} onClick={() => buy(p)} disabled={!!buying} className="rounded-xl border border-white/10 bg-white/[0.02] p-2.5 text-center hover:border-amber-400/40 transition-colors" data-testid={`wallet-pack-${p.credits}`}>
                      <div className="font-heading font-bold text-slate-50 text-base">{buying === p.id ? <Loader2 size={14} className="mx-auto animate-spin" /> : p.credits}</div>
                      <div className="text-[10px] text-slate-400">gettoni</div>
                      <div className="text-[11px] text-amber-300 font-semibold mt-1">€{p.price.toFixed(2).replace(".", ",")} + IVA</div>
                    </button>
                  ))}
                </div>
                <Button variant="ghost" className="w-full mt-2 h-8 text-xs text-slate-400 hover:text-cyan-200" onClick={() => toast("Scegli un pacchetto qui sopra per aggiungere crediti al tuo piano")} data-testid="wallet-add-credits">
                  <Plus size={12} className="mr-1" /> Aggiungi gettoni
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, big, sub, testId }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`font-heading font-bold text-slate-50 ${big ? "text-2xl" : "text-base"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); } catch { return "-"; }
};
